export type ReissueAssignmentState = {
  mainSurveyorId?: string;
  additionalSurveyorIds?: string[];
};

export function uniqueNonEmpty(values: Array<string | null | undefined>) {
  return Array.from(
    new Set(
      (values || []).map((value) => `${value || ''}`.trim()).filter(Boolean),
    ),
  );
}

export function getVisibleAdditionalSurveyorIds({
  additionalSurveyorIds,
  mainSurveyorId,
  canManageMultipleSurveyors,
}: {
  additionalSurveyorIds?: string[];
  mainSurveyorId?: string;
  canManageMultipleSurveyors: boolean;
}) {
  if (!canManageMultipleSurveyors) {
    return [];
  }

  return uniqueNonEmpty(additionalSurveyorIds || []).filter(
    (uid) => uid !== mainSurveyorId,
  );
}

export function getSelectedSurveyorIdsForReissue({
  mainSurveyorId,
  additionalSurveyorIds,
  canManageMultipleSurveyors,
}: ReissueAssignmentState & {
  canManageMultipleSurveyors: boolean;
}) {
  const selectedMainSurveyorId = `${mainSurveyorId || ''}`.trim();
  const selectedAdditionalSurveyorIds = uniqueNonEmpty(
    additionalSurveyorIds || [],
  ).filter((uid) => uid !== selectedMainSurveyorId);

  return selectedMainSurveyorId
    ? [selectedMainSurveyorId, ...selectedAdditionalSurveyorIds]
    : selectedAdditionalSurveyorIds;
}

export function getReissueAssignmentValidation({
  assignment,
  surveyorsById,
  canManageMultipleSurveyors,
}: {
  assignment: ReissueAssignmentState;
  surveyorsById: Record<string, unknown>;
  canManageMultipleSurveyors: boolean;
}) {
  const mainSurveyorId = `${assignment.mainSurveyorId || ''}`.trim();
  const visibleAdditionalSurveyorIds = canManageMultipleSurveyors
    ? assignment.additionalSurveyorIds || []
    : [];
  const mainSurveyorUnavailable =
    !!mainSurveyorId && !surveyorsById[mainSurveyorId];
  const hasInvalidAdditionalSelections = visibleAdditionalSurveyorIds.some(
    (uid) => uid && !surveyorsById[uid],
  );
  const hasEmptyAdditionalSelection = visibleAdditionalSurveyorIds.some(
    (uid) => !uid,
  );

  return {
    mainSurveyorUnavailable,
    hasInvalidAdditionalSelections,
    hasEmptyAdditionalSelection,
    canConfirm:
      !!mainSurveyorId &&
      !mainSurveyorUnavailable &&
      !hasInvalidAdditionalSelections &&
      !hasEmptyAdditionalSelection,
  };
}
